import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { type SensitiveSnapshot, SensitiveResource } from './mediaAccess.ts';
import { useWorkspace } from './workspace.tsx';

/**
 * A sensitive GET bound to the workspace's media-access epoch (see SensitiveResource).
 * `refreshKey` (for example the call's transcription state) reloads the same path when it changes.
 */
export function useSensitiveResource<T>(path: string | null, refreshKey: string | null = null): SensitiveSnapshot<T> & { reload: () => void } {
  const ws = useWorkspace();
  const [resource] = useState(() => new SensitiveResource<T>(ws.api, ws.mediaAccessStore, path));
  const lastKey = useRef(refreshKey);
  useEffect(() => {
    resource.activate();
    return () => resource.deactivate();
  }, [resource]);
  useEffect(() => {
    resource.setPath(path);
    if (lastKey.current !== refreshKey) {
      lastKey.current = refreshKey;
      resource.reload();
    }
  }, [resource, path, refreshKey]);
  const snapshot = useSyncExternalStore(resource.subscribe, resource.getSnapshot, resource.getSnapshot);
  return { ...snapshot, reload: () => resource.reload() };
}
